// LendmarqLoanTermsSheetPDF.jsx
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { isNil, isEmpty } from "lodash";
import { useMemo } from "react";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import { formatAddressObjectFullAddress } from "../../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import { formatBorrowerObjectFullName } from "../../../_src_shared/utils/functions/formatBorrowerObjectFullName";
import { formatDollar } from "../../../utils/numbers/formatDollar";
import { formatPercent } from "../../../utils/numbers/formatPercent";

import MontserratBold from "../../../assets/fonts/Montserrat/Montserrat-Bold.ttf";
import MontserratMedium from "../../../assets/fonts/Montserrat/Montserrat-Medium.ttf";
import MontserratRegular from "../../../assets/fonts/Montserrat/Montserrat-Regular.ttf";
import MontserratSemiBold from "../../../assets/fonts/Montserrat/Montserrat-SemiBold.ttf";
import MontserratBoldItalic from "../../../assets/fonts/Montserrat/Montserrat-BoldItalic.ttf";
import MontserratItalic from "../../../assets/fonts/Montserrat/Montserrat-Italic.ttf";

import lendmarqPdfHeader from "./images/lendmarq-pdf-header.jpg";
import internetIcon from "./images/internet-icon.png";
import locationIcon from "./images/location-icon.png";

// ─── Font Registration ───
Font.register({
  family: "Montserrat",
  fonts: [
    { src: MontserratRegular, fontWeight: 400, fontStyle: "normal" },
    { src: MontserratMedium, fontWeight: 500, fontStyle: "normal" },
    { src: MontserratSemiBold, fontWeight: 600, fontStyle: "normal" },
    { src: MontserratBold, fontWeight: 700, fontStyle: "normal" },
    { src: MontserratItalic, fontWeight: 400, fontStyle: "italic" },
    { src: MontserratBoldItalic, fontWeight: 700, fontStyle: "italic" },
  ],
});

// ─── Styles ───
const s = StyleSheet.create({
  page: {
    fontFamily: "Montserrat",
    fontSize: 11,
  },
  headerImage: {
    width: "100%",
    height: 120,
  },
  subHeaderRow: {
    flexDirection: "row",
    width: "100%",
    paddingTop: 20,
  },
  subHeaderItem: {
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 8,
    paddingBottom: 8,
    flexGrow: 1,
    justifyContent: "center",
  },
  primaryContent: {
    width: "100%",
    paddingLeft: 28,
    paddingRight: 28,
    paddingTop: 16,
    paddingBottom: 16,
  },
  contentArea: {
    width: "100%",
    paddingTop: 16,
    paddingBottom: 16,
  },
  twoColRow: {
    flexDirection: "row",
    gap: 24,
  },
  column: {
    flex: 1,
  },
  dataItemRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  titleText: {
    fontWeight: 700,
    fontSize: 14,
  },
  bodyText: {
    fontSize: 11,
    fontWeight: 400,
  },
  signatureBox: {
    borderBottom: "1px solid #000000",
  },
  footer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    backgroundColor: "#42659b",
    color: "#ffffff",
  },
  footerInner: {
    display: "flex",
    flexGrow: 1,
    gap: 8,
    alignItems: "center",
    flexDirection: "row",
    padding: 16,
  },
  footerItem: {
    display: "flex",
    flexGrow: 1,
    gap: 8,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
  disclaimer: {
    lineHeight: "13px",
    fontSize: 7,
  },
});

// ──────────────────────────────────────────────
// INLINE PRIMITIVES
// ──────────────────────────────────────────────

const PdfBox = ({ children, style }) => <View style={style}>{children}</View>;

const PdfRow = ({ children, style, gap = 0, marginBottom = 0 }) => (
  <View
    style={{
      flexDirection: "row",
      alignItems: "center",
      gap,
      marginBottom,
      ...style,
    }}
  >
    {children}
  </View>
);

const PdfColumn = ({ children, style, gap = 0 }) => (
  <View style={{ flexDirection: "column", gap, ...style }}>{children}</View>
);

const PdfText = ({
  children,
  fontSize = 11,
  fontWeight = 400,
  bold,
  style,
}) => (
  <Text
    style={{
      fontSize,
      fontWeight: bold ? 700 : fontWeight,
      ...style,
    }}
  >
    {children}
  </Text>
);

const PdfImage = ({ src, width, height, style }) => (
  <Image style={{ width, height, ...style }} src={src} />
);

const TitleText = ({ children, center = false, fontSize = 14, style }) => (
  <PdfText
    fontWeight={700}
    fontSize={fontSize}
    style={{ textAlign: center ? "center" : "left", ...style }}
  >
    {children}
  </PdfText>
);

const ContentArea = ({ children, style }) => (
  <PdfBox style={{ ...s.contentArea, ...style }}>{children}</PdfBox>
);

const SignatureBox = ({ width = "160px", style }) => (
  <PdfBox style={{ width, borderBottom: "1px solid #000000", ...style }} />
);

/** Single data row — skips rendering when hideEmpty is true and value is nil */
const DataItem = ({
  value,
  label,
  style,
  gap = 4,
  rowStyles,
  textStyles,
  marginBottom = 0,
  customValue,
  hideEmpty = false,
}) => {
  const displayedValue = isNil(value) || value === "" ? null : value;

  // If hideEmpty and no value/customValue, don't render
  if (hideEmpty && !displayedValue && !customValue) return null;

  return (
    <PdfRow
      style={{ flexWrap: "wrap", ...rowStyles }}
      gap={gap}
      marginBottom={marginBottom}
    >
      <PdfText fontWeight={700} fontSize={11} style={textStyles}>
        {label}:
      </PdfText>
      {customValue ? (
        customValue
      ) : (
        <PdfText fontSize={11} style={textStyles}>
          {displayedValue ?? "—"}
        </PdfText>
      )}
    </PdfRow>
  );
};

const SubHeaderRowItem = ({
  children,
  style,
  center = false,
  width,
  textStyle,
}) => (
  <PdfBox
    style={{
      ...s.subHeaderItem,
      alignItems: center ? "center" : "flex-start",
      ...(width ? { width, flexGrow: 0 } : {}),
      ...style,
    }}
  >
    <TitleText center={center} style={textStyle}>
      {children}
    </TitleText>
  </PdfBox>
);

const SignatureStandardBlock = ({ name }) => (
  <PdfColumn gap={6}>
    <DataItem label="By" customValue={<SignatureBox width="185px" />} />
    <DataItem label="Name" value={name} />
    <DataItem
      label="Today's Date"
      customValue={<SignatureBox width="150px" />}
    />
  </PdfColumn>
);

const SignatureInitialsBlock = () => (
  <DataItem
    marginBottom={0}
    label="Initials"
    style={{ lineHeight: "15px", fontSize: 12 }}
    customValue={
      <PdfRow marginBottom={0}>
        <SignatureBox width="50px" />
        <SignatureBox width="50px" style={{ marginLeft: 1 }} />
      </PdfRow>
    }
  />
);

const PdfFooter = () => (
  <PdfBox style={s.footer}>
    <PdfBox style={s.footerInner}>
      <PdfBox style={s.footerItem}>
        <PdfImage src={internetIcon} width={18} height={18} />
        <PdfText style={{ color: "#ffffff" }}>lendmarq.com</PdfText>
      </PdfBox>
      <PdfBox style={s.footerItem}>
        <PdfImage src={locationIcon} width={18} height={18} />
        <PdfText style={{ color: "#ffffff" }}>
          1100 Brickell Bay Dr. Suite 1604, Miami, FL 33131
        </PdfText>
      </PdfBox>
    </PdfBox>
  </PdfBox>
);

// ──────────────────────────────────────────────
// PAGE TEMPLATE
// ──────────────────────────────────────────────

const PdfPageTemplateWrapper = ({
  children,
  data,
  isSecondaryPage = false,
}) => (
  <Page size="A4" style={s.page}>
    <PdfImage src={lendmarqPdfHeader} width="100%" height={120} />

    {isSecondaryPage ? (
      <PdfBox style={{ ...s.subHeaderRow, width: "270px" }}>
        <SubHeaderRowItem
          textStyle={{ color: "#ffffff", fontStyle: "italic" }}
          style={{
            paddingLeft: 54,
            backgroundColor: "#42659b",
            borderTopRightRadius: 24,
            borderBottomRightRadius: 24,
          }}
        >
          Loan Details
        </SubHeaderRowItem>
      </PdfBox>
    ) : (
      <PdfBox style={s.subHeaderRow}>
        <SubHeaderRowItem
          center
          textStyle={{ color: "#ffffff", fontStyle: "italic" }}
          style={{
            backgroundColor: "#42659b",
            borderTopRightRadius: 24,
            borderBottomRightRadius: 24,
          }}
        >
          Loan Details
        </SubHeaderRowItem>
        <SubHeaderRowItem center style={{ flexGrow: 1 }}>
          {data?.entityName}
        </SubHeaderRowItem>
        <SubHeaderRowItem width={150} center>
          {formatDate(new Date())}
        </SubHeaderRowItem>
      </PdfBox>
    )}

    <PdfBox style={s.primaryContent}>{children}</PdfBox>
    <PdfFooter />
  </Page>
);

// ──────────────────────────────────────────────
// PAGES
// ──────────────────────────────────────────────

const PageOne = ({ data, hideEmpty }) => (
  <PdfPageTemplateWrapper data={data}>
    <ContentArea style={{ paddingTop: 12, paddingBottom: 12 }}>
      <PdfColumn gap={5}>
        <TitleText>{`${data?.loanPurpose || ""} - ${data?.loanProductType || ""}`}</TitleText>
        {data?.propertyAddresses?.map((address, idx) => (
          <DataItem
            key={idx}
            textStyles={{ fontSize: 13 }}
            label="Property Address"
            value={address}
            hideEmpty={hideEmpty}
          />
        ))}
      </PdfColumn>
    </ContentArea>

    <ContentArea>
      <PdfRow gap={24} style={{ alignItems: "flex-start" }}>
        <PdfColumn gap={9} style={{ flex: 1 }}>
          <DataItem
            label="Lien"
            value={data?.lienPosition}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Property Type"
            value={data?.propertyType}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Purchase Price"
            value={formatDollar(data?.purchasePrice)}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Construction Budget"
            value={formatDollar(data?.constructionBudget)}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Estimated As-Is Value"
            value={formatDollar(data?.estimatedAsIsValue)}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Estimated ARV"
            value={formatDollar(data?.estimatedArv)}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Occupancy"
            value={data?.occupancy}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Exit Strategy"
            value={data?.exitStrategy}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Total Loan Amount"
            value={formatDollar(data?.totalLoanAmount)}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Initial Loan Amount"
            value={formatDollar(data?.initialLoanAmount)}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Construction Holdback"
            value={formatDollar(data?.constructionHoldback)}
            hideEmpty={hideEmpty}
          />
        </PdfColumn>

        <PdfColumn gap={9} style={{ flex: 1, marginLeft: 10 }}>
          <DataItem
            label="Loan Term"
            value={data?.loanTerm}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Interest Rate"
            value={formatPercent(data?.interestRate)}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Interest Type"
            value={data?.interestType}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Amortization"
            value={data?.amortization}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Origination Fee"
            value={formatDollar(data?.originationFee)}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Legal Fee"
            value={formatDollar(data?.legalFee)}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Underwriting Fee"
            value={formatDollar(data?.underwritingFee)}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Payment Reserve"
            value={data?.paymentReserve}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Loan to Cost"
            value={formatPercent(data?.loanToCost)}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Loan to Value"
            value={formatPercent(data?.loanToValue)}
            hideEmpty={hideEmpty}
          />
          <DataItem
            label="Estimated ARV LTV"
            value={formatPercent(data?.estimatedArvLtv)}
            hideEmpty={hideEmpty}
          />
        </PdfColumn>
      </PdfRow>
    </ContentArea>

    <ContentArea style={{ paddingTop: 64, paddingBottom: 52 }}>
      <PdfBox style={{ alignItems: "center" }}>
        <SignatureInitialsBlock />
      </PdfBox>
    </ContentArea>

    <ContentArea>
      <TitleText
        center
        style={{
          lineHeight: "13px",
          fontSize: 12,
          textAlign: "center",
          fontStyle: "italic",
        }}
      >
        [Signatures Required on Following Page]
      </TitleText>
    </ContentArea>

    <ContentArea style={{ paddingTop: 0 }}>
      <PdfText style={s.disclaimer}>
        This term sheet is for discussion purposes only and does not constitute
        a loan commitment, approval, or obligation to lend or borrow. All terms,
        including loan amount, are estimates only and remain subject to receipt
        of a complete application, underwriting, credit approval, and
        third-party due diligence, all in the lender's sole discretion.
        Estimated amounts exclude third-party settlement and due-diligence
        costs, which vary by transaction and jurisdiction. No reliance may be
        placed on the information herein, and the lender may discontinue its
        review at any time without liability. Legal, third-party, and service
        fees may be required prior to or in connection with delivery of loan
        documents. Minimum profitability and return requirements apply.
      </PdfText>
    </ContentArea>
  </PdfPageTemplateWrapper>
);

const PageTwo = ({ data }) => (
  <PdfPageTemplateWrapper data={data} isSecondaryPage>
    <ContentArea>
      <PdfColumn gap={8}>
        <TitleText>Expense Agreement:</TitleText>
        <PdfText>
          By ordering these services you understand ALL of the below:
        </PdfText>

        <PdfText style={{ marginTop: 0, lineHeight: "15px" }}>
          Client agrees to be personally responsible for and to guarantee
          payment of all third-party vendor expenses incurred by Lender ISAOA
          and/or any participating lender in connection with the proposed
          financing. Deposits or payments may be insufficient to cover all such
          expense and may be applied or adjusted at closing in the lender's
          discretion. Any pricing estimates may not reflect all required vendor
          costs. Rush fees are nonrefundable, and any unused deposit funds may
          be returned at Lender ISAOA's discretion, subject to a $50 processing
          fee per item for loans that do not close.
        </PdfText>
        <PdfText style={{ marginTop: 8, lineHeight: "15px" }}>
          If a loan does not close, Client remains responsible for all fees
          incurred, which shall be invoiced to Client. Any invoice outstanding
          for more than 10 calendar days shall accrue interest at the maximum
          rate permitted by applicable law, together with all reasonable legal
          and collection costs.
        </PdfText>
      </PdfColumn>
    </ContentArea>

    <ContentArea style={{ paddingTop: 24 }}>
      <PdfColumn gap={16}>
        <TitleText>{data?.entityName}</TitleText>
        <PdfColumn gap={12} style={{ marginTop: 8 }}>
          <PdfRow
            style={{
              flexWrap: "wrap",
              justifyContent: "space-between",
              gap: 36,
            }}
          >
            {data?.guarantorNames?.map((name, idx) => (
              <SignatureStandardBlock key={idx} name={name} />
            ))}
          </PdfRow>
        </PdfColumn>
      </PdfColumn>
    </ContentArea>
  </PdfPageTemplateWrapper>
);

// ──────────────────────────────────────────────
// MAIN DOCUMENT
// ──────────────────────────────────────────────

export const mapLoanDataToLetterOfIntent = ({
  formData,
  loanData,
  loanTerms,
}) => {
  const guarantors = (formData?.borrowers || []).filter((b) => b.isGuarantor);
  const subjectProperties = formData?.subjectProperties || [];

  return {
    // Guarantors & Properties (pre-formatted string arrays)
    guarantorNames: guarantors.map((g) => formatBorrowerObjectFullName(g)),
    propertyAddresses: subjectProperties.map((p) =>
      formatAddressObjectFullAddress(p.address),
    ),
    propertyType: subjectProperties[0]?.propertyType,

    // Editable loan inputs
    loanProductType: formData?.loanProductType,
    loanPurpose: formData?.loanPurpose,
    exitStrategy: formData?.exitStrategy,
    loanTerm: formData?.termMonths,
    interestRate: formData?.interestRate,

    // Auto-calculated (non-editable) values
    totalLoanAmount: formData?.totalLoanAmount,
    initialLoanAmount: formData?.baseLoanAmount,
    purchasePrice: formData?.totalPurchasePrice,
    loanToCost: formData?.baseLoanToCost,
    loanToValue: formData?.baseLoanToAsIsValue,
    estimatedArvLtv: formData?.totalLoanToArv,
    estimatedAsIsValue: formData?.totalAsIsValue,
    estimatedArv: formData?.totalArv,
    constructionHoldback: formData?.totalHoldback,
    constructionBudget: formData?.totalConstructionBudget,
    entityName: formData?.borrowerEntity?.entityName,
    originationFee: loanTerms?.totalOriginatorFees,
    interestType: formData?.dutchInterest ? "Dutch" : "Non-Dutch (as drawn)",
    amortization: formData?.amortizationType || "Interest Only",

    // Hard-coded (no input fields exist yet)
    occupancy: "Non-Owner Occupied Investment",
    legalFee: 0,
    underwritingFee: 0,
    lienPosition: "1st Position",
    paymentReserve: "N/A",
  };
};

/**
 * Normalizes legacy term sheet data to the current key shape.
 * Falls back to old keys when new keys are not present.
 */
const normalizeLegacyTermSheetData = (data) => {
  if (!data) return {};

  const normalized = { ...data };

  // estimatedAsIsValue ← asIsValue
  if (isNil(normalized.estimatedAsIsValue) && !isNil(normalized.asIsValue)) {
    normalized.estimatedAsIsValue = normalized.asIsValue;
  }

  // estimatedArvLtv ← arvLtv
  if (isNil(normalized.estimatedArvLtv) && !isNil(normalized.arvLtv)) {
    normalized.estimatedArvLtv = normalized.arvLtv;
  }

  // guarantorNames (string[]) ← guarantors (object[])
  if (isNil(normalized.guarantorNames) && !isEmpty(normalized.guarantors)) {
    normalized.guarantorNames = normalized.guarantors.map((g) =>
      typeof g === "string" ? g : formatBorrowerObjectFullName(g),
    );
  }

  // propertyAddresses (string[]) ← subjectProperties (object[])
  if (
    isNil(normalized.propertyAddresses) &&
    !isEmpty(normalized.subjectProperties)
  ) {
    normalized.propertyAddresses = normalized.subjectProperties.map((p) =>
      typeof p === "string" ? p : formatAddressObjectFullAddress(p.address),
    );
  }

  return normalized;
};

const LendmarqLoanTermsSheetPDF = ({
  formData,
  convertedData,
  loanTerms,
  hideEmpty = false,
}) => {
  console.log({ formData, convertedData });
  const termSheetData = useMemo(() => {
    if (convertedData) {
      return normalizeLegacyTermSheetData(convertedData);
    }
    return mapLoanDataToLetterOfIntent({ formData, loanTerms });
  }, [formData, loanTerms, convertedData]);

  return (
    <Document>
      <PageOne data={termSheetData} hideEmpty={hideEmpty} />
      <PageTwo data={termSheetData} />
    </Document>
  );
};
export default LendmarqLoanTermsSheetPDF;
